import { Delete, Star, StarBorderOutlined } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import usePagebuilder from '../../../../hooks/pagebuilder/usePagebuilder';
import { useCustomTranslations } from '../../../../hooks/useCustomTranslations';
import { useRole } from '../../../../hooks/useRole';
import { AgendaEntry, SpeakerEntry } from '../../../../interfaces/pagebuilder';
import { fetchAndSetFavoriteAgendaEntries, setAgendaEntryModal } from '../../../../reduxToolkit/slices/commonSlice';
import { addFavoriteAgendaEntry, deleteAgendaEntry, deleteFavoriteAgendaEntry } from '../../../../services/services';
import { VyConfirm } from '../../../common/VyConfirm';
import VyTooltip from '../../../common/VyTooltip';
import InlineEditButton from '../../pageBuilder/InlineEditButton';
import EntryCalendarExport from '../VystemLight/UIComponents/AgendaUiComponent/EntryCalendarExport';
import EntryInlineEdit from '../VystemLight/UIComponents/AgendaUiComponent/EntryInlineEdit';
import Link from 'next/link';
import { useDispatch, useSelector } from '../../../../reduxToolkit/typedRedux';

interface MBWAgendaEntryProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  entry: AgendaEntry;
  openSpeaker: (speaker: SpeakerEntry) => void;
  uiComponent: Partial<any>;
  withHoverAnimation?: boolean;
  customFontSize?: number;
}

const MBWAgendaEntry = ({ entry, openSpeaker, uiComponent, withHoverAnimation = true, customFontSize, ...props }: MBWAgendaEntryProps) => {
  const favoriteAgendaEntries = useSelector((state) => state.common.favoriteAgendaEntries);
  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);
  const templateEditMode = useSelector((state) => state.common.templateEditMode);
  const eventSpeakers = useSelector((state) => state.common.eventSpeakers);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const { ct } = useCustomTranslations();
  const { t } = useTranslation();
  const { isUserEventHost } = useRole();
  const { editComponent } = usePagebuilder(true);

  const date = new Date(entry.timestamp || new Date().toISOString());
  const { allowEntriesFavoriting: withFavorite, _id: componentid, allowCalendarExport } = uiComponent;

  async function addFavorite() {
    if (!componentid || !entry._id || !event) return;
    await addFavoriteAgendaEntry({
      componentid,
      entryid: entry._id,
      eventid: event?._id,
    });
    event && dispatch(fetchAndSetFavoriteAgendaEntries(event?._id));
  }

  async function deleteEntry() {
    if (!entry._id) return;
    setIsLoading(true);
    try {
      await deleteAgendaEntry(entry._id);
      const entryList = uiComponent.entryList?.filter((e: AgendaEntry) => e._id !== entry._id);
      editComponent({ ...uiComponent, entryList });
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  }

  async function deleteFavorite() {
    if (!componentid || !entry._id) return;
    await deleteFavoriteAgendaEntry(entry._id);
    event && dispatch(fetchAndSetFavoriteAgendaEntries(event?._id));
  }

  const isFavorite = withFavorite ? favoriteAgendaEntries.find((e) => e.entry._id === entry._id) : null;

  const { detailsText = '', linkText = '' } = entry.buttonTexts || {};

  function customFontStyle(ratio = 1, color?: string) {
    return { ...(customFontSize ? { fontSize: `${customFontSize / ratio}px` } : {}), ...(color ? { color } : {}) };
  }

  if (isEditMode && templateEditMode && isUserEventHost()) return <EntryInlineEdit setIsEditMode={setIsEditMode} entry={entry} uiComponent={uiComponent} />;

  const allEventSpeakerIds = eventSpeakers?.map((s) => s._id);
  const speakersToDisplay = entry.speakers?.filter((s) => allEventSpeakerIds?.includes(s._id)) ?? [];

  if (entry?.isTitle) return <span className="pt-4 pb-4 pl-4 pr-4 text-xl font-bold">{entry?.title}</span>;
  return (
    <div id={entry._id} className="relative flex w-full">
      {isUserEventHost() && templateEditMode && !isEditMode && (
        <div className="absolute top-0 right-0 z-40 flex space-x-2">
          <InlineEditButton setIsEditMode={setIsEditMode} isEditMode={isEditMode} forceShow tooltip="" className="" />
          <VyConfirm disabled={isLoading} onConfirm={deleteEntry}>
            <button disabled={isLoading} className="z-40 grid w-10 h-10 text-black bg-white bg-opacity-25 rounded-full place-items-center">
              {isLoading ? <CircularProgress /> : <Delete />}
            </button>
          </VyConfirm>
        </div>
      )}
      <div className={`flex items-center ${withHoverAnimation ? 'transition duration-200 hover:scale-105 transform ease-in-out' : ''}`}>
        {withFavorite && user && (
          <div className="flex h-full pt-4">
            <div className="flex cursor-pointer">
              <VyTooltip title={isFavorite ? t('event:remove-from-my-agenda') : t('event:add-to-my-agenda')}>
                {!isFavorite ? (
                  <StarBorderOutlined onClick={() => addFavorite()} style={{ fill: '#FFCA28' }} />
                ) : (
                  <Star onClick={() => deleteFavorite()} style={{ fill: '#FFCA28' }} />
                )}
              </VyTooltip>
            </div>
          </div>
        )}
        <div {...props} className={`flex flex-col m-1 md:flex-row md:m-4 cursor-default`}>
          {entry?.hideTime === true ? (
            <p style={{ minWidth: '48px' }}></p>
          ) : (
            <p style={{ ...customFontStyle(undefined, entry.titleColor), lineHeight: '1.5rem' }} className="text-2xl font-bold w-19">
              {format(date, 'HH:mm')}
            </p>
          )}
          <div className="md:ml-4">
            <p
              style={{ ...customFontStyle(undefined, entry.titleColor), lineHeight: '1.5rem', paddingRight: templateEditMode ? '5rem' : 0 }}
              className="font-bold break-normal texl-xl md:text-2xl">
              {ct(entry.title, entry, 'titleTranslations')}
            </p>
            <p style={{ ...customFontStyle(1.2, entry.descriptionColor) }} className="text-sm md:text-base">
              <span dangerouslySetInnerHTML={{ __html: ct(entry.description, entry, 'descriptionTranslations') || '' }}></span>
              {speakersToDisplay && speakersToDisplay.length > 0 && (
                <div className={`flex flex-col space-y-3 ${entry.description ? 'mt-3' : ''}`}>
                  {speakersToDisplay.map((speaker, index) => (
                    <span
                      className={speaker.hasModal ? `transition-colors cursor-pointer` : ''}
                      onClick={() => speaker && speaker.hasModal && openSpeaker(speaker)}
                      dangerouslySetInnerHTML={{
                        __html: speaker.name + (speaker.position ? `, ${ct(speaker?.position, speaker, 'positionTranslation')}` : ''),
                      }}></span>
                  ))}
                </div>
              )}
            </p>
            {(entry.image || entry.additionalText) && (
              <p
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setAgendaEntryModal(entry));
                }}
                className="cursor-pointer link">
                ► {detailsText?.length > 0 ? detailsText : t('pagebuilder:open-details')}
              </p>
            )}

            {entry.url && (
              <Link href={entry.url} target="_blank">
                <p className="cursor-pointer link">► {linkText?.length > 0 ? linkText : t('pagebuilder:visit-url')}</p>
              </Link>
            )}
            {allowCalendarExport && <EntryCalendarExport entry={entry} date={date} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MBWAgendaEntry;
